/* Iter122dv · Card "Profilo Studio · Titolare del trattamento" (GDPR).
   Estratto da pages/Impostazioni.js per riuso in pagina unificata "Impostazioni & Account". */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Save, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ProfileStudioCard() {
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    ragione_sociale: "", indirizzo: "", citta: "", cap: "", provincia: "",
    telefono: "", email: "", pec: "", codice_fiscale: "", partita_iva: "",
    legale_rappresentante: "", email_contabilita: "",
  });
  const [mancanti, setMancanti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/me/tenant/titolare").then((r) => {
      const d = r.data || {};
      setForm({
        ragione_sociale: d.ragione_sociale || "", indirizzo: d.indirizzo || "",
        citta: d.citta || "", cap: d.cap || "", provincia: d.provincia || "",
        telefono: d.telefono || "", email: d.email || "", pec: d.pec || "",
        codice_fiscale: d.codice_fiscale || "", partita_iva: d.partita_iva || "",
        legale_rappresentante: d.legale_rappresentante || "",
        email_contabilita: d.email_contabilita || "",
      });
      setMancanti(d.campi_mancanti || []);
    }).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const salva = async () => {
    setSaving(true);
    try {
      // Iter122et · Salvataggio "difensivo": invia SOLO i campi con valore non vuoto
      // così un salvataggio parziale non azzera campi già persistiti in DB. Inoltre
      // dopo il PUT, ricarichiamo davvero dal server (load) e verifichiamo che
      // almeno un campo inviato risulti persistito nella risposta — altrimenti
      // avvisiamo l'utente in modo esplicito (non un semplice "successo" muto).
      const payload = Object.fromEntries(
        Object.entries(form)
          .map(([k, v]) => [k, (v || "").trim()])
          .filter(([, v]) => v !== "")
      );
      if (Object.keys(payload).length === 0) {
        toast.warning("Inserisci almeno un campo prima di salvare.");
        setSaving(false);
        return;
      }
      const { data } = await api.put("/me/tenant/titolare", payload);
      // Ricarichiamo da server per riflettere lo stato REALE dopo il write
      await load();
      // Diagnostica: verifica che ogni campo appena inviato sia presente nella
      // risposta del server (persistito). Se qualcosa non è tornato indietro,
      // segnala l'anomalia con un toast di errore (mai un falso "successo").
      const mancantiKeys = new Set((data.campi_mancanti || []).map((m) => m.key));
      const nonPersistiti = Object.keys(payload).filter(
        (k) => (payload[k] || "").trim() !== "" && mancantiKeys.has(k)
      );
      if (nonPersistiti.length > 0) {
        toast.error(
          `Il server ha risposto OK ma questi campi risultano ancora vuoti: ${nonPersistiti.join(", ")}. ` +
          `Se sei su PWA installata, chiudi l'app e riaprila (o svuota la cache) e riprova.`
        );
      } else if ((data.campi_mancanti || []).length === 0) {
        toast.success("Profilo Studio completo. Grazie!");
      } else {
        toast.success("Dati salvati. Puoi completare i restanti campi quando vuoi.");
      }
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito");
    }
    setSaving(false);
  };

  const isMissing = (key) => mancanti.some((m) => m.key === key);
  const inputClass = (key) => `h-11 text-base ${isMissing(key) ? "border-amber-400 focus-visible:ring-amber-400" : ""}`;

  return (
    <Card className="p-5 sm:p-6" data-testid="profilo-studio-card">
      <div className="mb-5">
        <h2 className="font-bold text-xl text-slate-800">Profilo Studio · Titolare del trattamento</h2>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
          Questi dati identificano il Suo Studio come <b>Titolare del trattamento</b> ex Reg. UE 2016/679 (GDPR).
          Vengono mostrati ai condòmini e ai fornitori nelle informative privacy, nelle email di
          comunicazione e nei documenti generati dalla piattaforma.
        </p>
      </div>
      {!loading && (
        mancanti.length === 0 ? (
          <div className="mb-5 rounded-lg border-2 border-green-300 bg-green-50 p-3 flex items-center gap-2 text-green-800" data-testid="profilo-completo-badge">
            <CheckCircle2 className="w-5 h-5 shrink-0" />
            <span className="text-sm font-semibold">Profilo completo · tutti i campi obbligatori sono compilati.</span>
          </div>
        ) : (
          <div className="mb-5 rounded-lg border-2 border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-amber-900" data-testid="profilo-mancanti-alert">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold">Mancano {mancanti.length} campi obbligatori:</div>
              <div className="mt-1">{mancanti.map((m) => m.label).join(" · ")}</div>
            </div>
          </div>
        )
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label htmlFor="ragione_sociale" className="text-sm font-semibold text-slate-700">Ragione sociale *</Label>
          <Input id="ragione_sociale" data-testid="input-ragione-sociale" className={inputClass("ragione_sociale")}
            value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })}
            placeholder="Es. Studio Rossi & Associati Srl" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="indirizzo" className="text-sm font-semibold text-slate-700">Indirizzo (via, n. civico) *</Label>
          <Input id="indirizzo" data-testid="input-indirizzo" className={inputClass("indirizzo")}
            value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })}
            placeholder="Es. Via Roma 42" />
        </div>
        <div><Label className="text-sm font-semibold text-slate-700">CAP *</Label>
          <Input data-testid="input-cap" className={inputClass("cap")} maxLength={5}
            value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} placeholder="20121" /></div>
        <div><Label className="text-sm font-semibold text-slate-700">Città *</Label>
          <Input data-testid="input-citta" className={inputClass("citta")}
            value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} placeholder="Milano" /></div>
        <div><Label className="text-sm font-semibold text-slate-700">Provincia *</Label>
          <Input data-testid="input-provincia" className={inputClass("provincia")} maxLength={2}
            value={form.provincia.toUpperCase()} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })}
            placeholder="MI" /></div>
        <div><Label className="text-sm font-semibold text-slate-700">Telefono</Label>
          <Input data-testid="input-telefono" className={inputClass("telefono")}
            value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="02 1234567" /></div>
        <div><Label className="text-sm font-semibold text-slate-700">Email di contatto *</Label>
          <Input type="email" data-testid="input-email" className={inputClass("email")}
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@studio.it" /></div>
        <div><Label className="text-sm font-semibold text-slate-700">PEC *</Label>
          <Input type="email" data-testid="input-pec" className={inputClass("pec")}
            value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} placeholder="studio@pec.example" /></div>
        <div><Label className="text-sm font-semibold text-slate-700">Codice fiscale *</Label>
          <Input data-testid="input-codice-fiscale" className={inputClass("codice_fiscale")}
            value={form.codice_fiscale.toUpperCase()} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })}
            placeholder="RSSMRA80A01F205X" /></div>
        <div><Label className="text-sm font-semibold text-slate-700">Partita IVA *</Label>
          <Input data-testid="input-partita-iva" className={inputClass("partita_iva")}
            value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} placeholder="12345678901" /></div>
        {/* Iter122k44 · Legale rappresentante — usato dall'Editor "Legale rapp.te" */}
        <div className="sm:col-span-2">
          <Label htmlFor="legale_rappresentante" className="text-sm font-semibold text-slate-700">Legale rappresentante (amministratore firmatario)</Label>
          <Input id="legale_rappresentante" data-testid="input-legale-rappresentante"
            className="h-11 text-base border-2 border-slate-300"
            value={form.legale_rappresentante}
            onChange={(e) => setForm({ ...form, legale_rappresentante: e.target.value })}
            placeholder="Es. Mario Rossi" />
          <div className="text-xs text-slate-500 mt-1">Questo nominativo viene inserito dal pulsante "Legale rapp.te" dell'editor comunicazioni.</div>
        </div>
        {/* Iter122k116 · Email dedicata alle note pro-forma / fatture Credius */}
        <div className="sm:col-span-2">
          <Label htmlFor="email_contabilita" className="text-sm font-semibold text-slate-700">
            Email di contabilità (note pro-forma Credius)
          </Label>
          <Input id="email_contabilita" type="email" data-testid="input-email-contabilita"
            className="h-11 text-base border-2 border-slate-300"
            value={form.email_contabilita}
            onChange={(e) => setForm({ ...form, email_contabilita: e.target.value })}
            placeholder="Es. contabilita@studio.it" />
          <div className="text-xs text-slate-500 mt-1">
            Indirizzo dedicato per ricevere le <b>note pro-forma Credius</b> quando il cliente della
            pratica (condominio) non ha un&apos;email di fatturazione dedicata. Se lasciato vuoto,
            il sistema userà l&apos;<i>Email di contatto</i> qui sopra come fallback finale.
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Button data-testid="salva-titolare-btn" onClick={salva} disabled={saving || loading}
          className="bg-brand hover:bg-brand-dark text-white h-11 px-6 font-semibold">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Salvataggio…" : "Salva profilo"}
        </Button>
      </div>
    </Card>
  );
}
